import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {AuthType, Config, Device, DeviceType, SourceConfig} from 'smag-commons/cfg/connector/mqtt';
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {BehaviorSubject, merge, Observable, of, Subject, Subscription} from 'rxjs';
import * as _ from 'lodash';
import {isDefined, keySorter} from '../../../commons/commons';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {
    validateFloat,
    validateGt,
    validateIfDirty,
    validateInteger,
    validateNotBlank,
    validatePOD,
    validateProductionPOD
} from '../../../commons/validators';
import {
    DEFAULT_MAX_CHARS,
    HOST_MAX_CHARS,
    HOST_PATTERN,
    ID_MAX_CHARS,
    ID_PATTERN,
    MQTT_AUTH_TYPES,
    MQTT_DEFAULT_ENERGY_PROFILE,
    MQTT_DEV_TYPES,
    MQTT_ENERGY_PROFILES,
    MQTT_MAX_K,
    wrapMqttDevice
} from '../connectors';
import {MatSelectChange} from '@angular/material/select';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {I18NService} from '../../../i18n/i18n.service';
import {SmagApiService} from '../../../api/smag-api.service';
import {MatRadioChange} from '@angular/material/radio';
import {Phases} from 'smag-commons/cfg';
import {MqttDeviceWrapper, MqttEnergyProfile, MqttMeasures} from '../mqtt';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {meterModel2MQTTDevice} from 'smag-commons/fcn';

interface MeasureConfig {
    measId: string;
    newlyCreated: boolean; // "newly" = "not saved yet"
}

interface VirtualDeviceConfig {
    vdevId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
    measures: { [measId: string]: MeasureConfig };
    newlyCreated: boolean; // "newly" = "not saved yet"
}

interface ProductionMeasures {
    plantIndex: number; // starts from 1
    measureIds: string[]; // at least one is present
}

interface PodSerial {
    pod: string;
    serial: string;
}

interface PagedVirtualDevices {
    data: VirtualDeviceConfig[];
    total: number;
}

const MAX_PLANTS = 4;

const PAGE_SIZE = 5;
const PAGE_SIZES = [5, 10, 15];

const PASSWORD_PLACEHOLDER = '**********';

@Component({
    selector: 'app-mqtt-config',
    templateUrl: './mqtt-config.component.html',
    styleUrls: ['./mqtt-config.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Devices per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class MqttConfigComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadPages: Subject<number>;
    @ViewChild('addOrRenameVirtualDevice') private addOrRenameVirtualDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteVirtualDevice') private deleteVirtualDeviceDialog: TemplateRef<any>;
    @ViewChild('paginator') private paginator: MatPaginator;
    @ViewChild('deviceSearch') deviceSearch: ElementRef;

    pagedVDevs: BehaviorSubject<PagedVirtualDevices>;

    @Input('id') id: string;
    @Input('config') config: Config;
    @Output('configChange') configEmitter: EventEmitter<Config> = new EventEmitter<Config>();
    @Output('valid') validEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    cfg: Config;
    supportedDevTypes: DeviceType[];
    form: FormGroup;
    virtualDevices: VirtualDeviceConfig[];
    vDevToExpand: string;
    deviceSearchCtrl: FormControl;
    filteredDevices: Observable<VirtualDeviceConfig[]>;
    selectedDev: VirtualDeviceConfig;

    // exported in HTML
    MQTT_AUTH_TYPES = MQTT_AUTH_TYPES;
    AuthType = AuthType;
    MqttEnergyProfile = MqttEnergyProfile;
    Phases = Phases;
    MQTT_ENERGY_PROFILES = MQTT_ENERGY_PROFILES;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    HOST_MAX_CHARS = HOST_MAX_CHARS;
    ID_MAX_CHARS = ID_MAX_CHARS;
    DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    MQTT_MAX_K = MQTT_MAX_K;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly appCfg: AppConfig,
                private readonly apiService: SmagApiService,
                private fb: FormBuilder,
                private readonly i18n: I18NService,
                private readonly dialog: MatDialog,
                private readonly dialogService: DialogService) {
        this.reloadPages = new Subject<number>();
        this.pagedVDevs = new BehaviorSubject<PagedVirtualDevices>(null);
        this.vDevToExpand = null;
        this.deviceSearchCtrl = this.fb.control('');
        this.selectedDev = null;
        this.supportedDevTypes = [];
    }

    ngOnInit() {
        // NOTE: config is not supposed to be changed externally outside of component's creation
        this.cfg = _.cloneDeep(this.config);
        this.form = this.fb.group({
            authType: new FormControl(this.cfg.src_cfg.auth_type, [Validators.required]),
            host: new FormControl(this.cfg.src_cfg.host, [validateNotBlank, Validators.pattern(HOST_PATTERN)]),
            port: new FormControl(this.cfg.src_cfg.port, [Validators.required, validateInteger, validateGt(0)])
        });
        this.updateSourceControls(this.cfg.src_cfg); // done *before* subscribing to changes
        this.virtualDevices = Object.keys(this.cfg.devices).map(vdevId => this.mkVirtualDeviceConfig(vdevId, false));
        this.virtualDevices.sort(keySorter('vdevId'));
        this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.updateSourceConfig(value);
            this.updateConfig();
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        // do not send an initial config update, as it may trigger a (useless) REST call
        this.updateValidity();
        this.filteredDevices = this.deviceSearchCtrl.valueChanges.pipe(
            startWith(''),
            map(event => typeof event === 'string' ? event : (event as VirtualDeviceConfig).vdevId),
            map(vdevId => vdevId ? this.filterDevices(vdevId) : this.virtualDevices.slice()),
            takeUntil(this.destroyed)
        );
        // load device types supported by this connector
        this.loadSupportedDevices();
    }

    ngAfterViewInit() {
        merge(this.paginator.page,
            this.reloadPages.pipe(tap(page => this.paginator.pageIndex = page))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedVirtualDevices(this.paginator.pageSize, this.paginator.pageIndex).pipe(
                    startWith(null as PagedVirtualDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedVDevs => {
            if (pagedVDevs) {
                setTimeout(() => this.pagedVDevs.next(pagedVDevs), 0);
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.complete();
        this.destroyed.next();
    }

    trackByIndex(index: number): number {
        return index;
    }

    trackByVdevId(index: number, vdevCfg: VirtualDeviceConfig): string {
        return vdevCfg.vdevId;
    }

    trackByMeasId(index: number, measCfg: MeasureConfig): string {
        return measCfg.measId;
    }

    displaySearchDevice(vdevCfg: VirtualDeviceConfig): string {
        return vdevCfg.vdevId;
    }

    getVdevPhases(vdevCfg: VirtualDeviceConfig): Phases {
        return this.cfg.devices[vdevCfg.vdevId]?.phases || Phases.SINGLE;
    }

    getVdevMqttMeasures(vdevCfg: VirtualDeviceConfig): MqttMeasures {
        const wrapper = this.getVdevWrapper(vdevCfg.vdevId);
        return wrapper ? wrapper.getMqttMeasures() : null;
    }

    getProductionMeasures(mqttMeasures: MqttMeasures): ProductionMeasures[] {
        const prodMeasures = mqttMeasures.productionMeasures.map((pm, i) => ({
            plantIndex: i + 1,
            measureIds: pm
        })).filter(pm => !!pm.measureIds.length);
        return prodMeasures.length ? prodMeasures : null;
    }

    getProductionPlantRows(prodMeasures: ProductionMeasures[]): ProductionMeasures[][] {
        const rows: ProductionMeasures[][] = [];
        let row: ProductionMeasures[];
        prodMeasures.forEach((prodMeas, idx) => {
            if (idx % 2 === 0) {
                row = [prodMeas];
                rows.push(row);
            } else {
                row.push(prodMeas);
            }
        });
        return rows;
    }

    getVdevMeasureConfigs(vdevCfg: VirtualDeviceConfig, measIds: string[]): MeasureConfig[] {
        return measIds.map(measId => vdevCfg.measures[measId]);
    }

    getMaxProductionPlants(vdevCfg: VirtualDeviceConfig): number {
        const wrapper = this.getVdevWrapper(vdevCfg.vdevId);
        return wrapper ? wrapper.maxProductionPlants : 1;
    }

    mkProductionPlantPodControlName(plantIndex: number): string {
        return `m2_${plantIndex}Pod`;
    }

    mkProductionPlantKControlName(plantIndex: number): string {
        return `m2_${plantIndex}K`;
    }

    formatMeasureType(vdevCfg: VirtualDeviceConfig, measId: string): string {
        const wrapper = this.getVdevWrapper(vdevCfg.vdevId);
        return wrapper.formatMeasureType(wrapper.getMeasureType(measId));
    }

    onDeviceSearchLoseFocus(event: FocusEvent) {
        // do NOT reset control if an actual device has just been selected
        if (!(event.relatedTarget as Element)?.getAttribute('data-connectorDevice') &&
            !this.virtualDevices.find(device => device.vdevId === this.deviceSearchCtrl.value.vdevId)) {
            // we have lost focus on (autocomplete) input, but not because we have selected an actual option:
            // simulate the dismissal of the constraint form
            this.resetDeviceSearch();
        }
    }

    onClickResetDeviceSearch() {
        this.resetDeviceSearch();
    }

    onSelectSearchDevice(event: MatAutocompleteSelectedEvent) {
        this.selectedDev = event.option.value as VirtualDeviceConfig;
        this.deviceSearch.nativeElement.blur();
    }

    onAuthTypeChange(event: MatSelectChange) {
        // clear source config
        this.cfg.src_cfg = {
            auth_type: event.value as AuthType,
            host: this.cfg.src_cfg.host,
            port: this.cfg.src_cfg.port
        };
        // update controls
        this.updateSourceControls(this.cfg.src_cfg);
    }

    onVirtualDeviceTypeChange(vdevCfg: VirtualDeviceConfig) {
        // set energy profile and production plants to default values
        vdevCfg.form.controls['energyProfile'].setValue(MQTT_DEFAULT_ENERGY_PROFILE,
            {onlySelf: true, emitEvent: false});
        vdevCfg.form.controls['productionPlants'].setValue(1,
            {onlySelf: true, emitEvent: false});
        this.resetProductionPods(vdevCfg);
        this.resetK(vdevCfg);
        this.setDefaultMeasures(vdevCfg, vdevCfg.form.controls['energyProfile'].value, this.getProductionPods(vdevCfg));
    }

    onVirtualDevicePhasesChange(vdevCfg: VirtualDeviceConfig) {
        this.resetK(vdevCfg);
    }

    onVirtualDeviceEnergyProfileChange(vdevCfg: VirtualDeviceConfig, event: MatRadioChange) {
        // set production plants to default value
        vdevCfg.form.controls['productionPlants'].setValue(1, {onlySelf: true, emitEvent: false});
        this.resetProductionPods(vdevCfg);
        this.resetK(vdevCfg);
        this.setDefaultMeasures(vdevCfg, event.value, this.getProductionPods(vdevCfg));
        this.fixDeviceMeter(vdevCfg);
    }

    onVirtualDeviceProductionPlantsChange(vdevCfg: VirtualDeviceConfig) {
        const productionPlantsCtrl = vdevCfg.form.controls['productionPlants'];
        if (!productionPlantsCtrl.invalid) {
            this.resetProductionPods(vdevCfg);
            this.resetK(vdevCfg);
            this.setDefaultMeasures(vdevCfg, vdevCfg.form.controls['energyProfile'].value, this.getProductionPods(vdevCfg));
        }
        this.fixDeviceMeter(vdevCfg);
    }

    onVirtualDeviceProductionPodChange(vdevCfg: VirtualDeviceConfig, plantIndex: number) {
        const productionPodCtrl = vdevCfg.form.controls[this.mkProductionPlantPodControlName(plantIndex)];
        if (!productionPodCtrl.invalid) {
            this.setDefaultMeasures(vdevCfg, vdevCfg.form.controls['energyProfile'].value, this.getProductionPods(vdevCfg));
        }
    }

    onClickAddVirtualDevice() {
        this.openAddOrRenameVirtualDeviceDialog().then(podSerial => {
            if (podSerial) {
                this.addVirtualDevice(mkDeviceId(podSerial.pod, podSerial.serial), podSerial.serial);
                this.resetDeviceSearch();
            }
        });
    }

    onClickRenameVirtualDevice(vdevCfg: VirtualDeviceConfig, event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        this.openAddOrRenameVirtualDeviceDialog(vdevCfg.vdevId).then(podSerial => {
            if (podSerial) {
                const newId = mkDeviceId(podSerial.pod, podSerial.serial);
                // check if we are actually changing name
                if (newId && newId !== vdevCfg.vdevId) {
                    this.renameVirtualDevice(vdevCfg, newId, podSerial.serial);
                    this.resetDeviceSearch();
                }
            }
        });
    }

    onClickDeleteVirtualDevice(vdevCfg: VirtualDeviceConfig) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Do you want to delete device {0} ?', vdevCfg.vdevId),
            template: this.deleteVirtualDeviceDialog,
            context: {
                vdevId: vdevCfg.vdevId,
                relatedMeasures: _.values(vdevCfg.measures).map(m => m.measId)
            }
        }).closed.then(del => {
            if (del) {
                this.deleteVirtualDevice(vdevCfg);
                this.resetDeviceSearch();
            }
        });
    }

    onPasswordControlInteraction() {
        const pwdControl = this.form.controls['pass'];
        if (!pwdControl.dirty) {
            pwdControl.markAsDirty();
            pwdControl.setValue(null); // remove placeholder
        }
    }

    private openAddOrRenameVirtualDeviceDialog(devId?: string): Promise<PodSerial> {
        const virtualDevices = new Set(this.virtualDevices.map(vdev => vdev.vdevId));
        const devPodSerial = parseDeviceId(devId);
        const form = this.fb.group({
            pod: new FormControl(devPodSerial.pod, [validateNotBlank, validatePOD]),
            serial: new FormControl(devPodSerial.serial, [validateNotBlank, Validators.pattern(ID_PATTERN)])
        }, {
            validators: [(ctrl) => {
                const deviceId = mkDeviceId((ctrl as FormGroup).controls.pod.value, (ctrl as FormGroup).controls.serial.value);
                return deviceId !== devId && virtualDevices.has(deviceId) ? {duplicate: true} : null;
            }]
        });
        return this.dialogService.open<any, boolean>({
            title: this.i18n.translate(!devId ? 'Create device ID' : 'Rename device ID'),
            template: this.addOrRenameVirtualDeviceDialog,
            context: {
                form,
                idMaker: () => mkDeviceId(form.controls.pod.value, form.controls.serial.value),
            }
        }).closed.then(add => {
            return add ? {
                pod: form.controls.pod.value?.trim(),
                serial: form.controls.serial.value?.trim()
            } : null;
        });
    }

    private get isValid(): boolean {
        return !this.form.invalid &&
            this.virtualDevices.every(vdev => !vdev.form.invalid);
    }

    private filterDevices(vdevId: string): VirtualDeviceConfig[] {
        const filterValue = vdevId.toLowerCase().trim();
        return this.virtualDevices.filter(vdevCfg => vdevCfg.vdevId.toLowerCase().indexOf(filterValue) >= 0);
    }

    private resetDeviceSearch() {
        this.selectedDev = null;
        this.deviceSearchCtrl.setValue('');
    }

    private updateConfig() {
        this.configEmitter.emit(this.cfg);
    }

    private updateValidity() {
        this.validEmitter.emit(this.isValid);
    }

    private updateSourceControls(srcCfg: SourceConfig) {
        // clear form controls and add source-specific controls
        Object.keys(this.form.controls)
            .filter(ctrl => ctrl !== 'authType' && ctrl !== 'host' && ctrl !== 'port')
            .forEach(ctrl => this.form.removeControl(ctrl));
        switch (srcCfg.auth_type) {
            case AuthType.CREDENTIALS:
                this.form.addControl('user', new FormControl(srcCfg.username, [validateNotBlank]));
                this.form.addControl('pass', new FormControl(srcCfg.password || PASSWORD_PLACEHOLDER, [validateIfDirty(validateNotBlank)]));
                break;
            case AuthType.CERTIFICATES:
                // NOTE: certificate file paths specified at YAML level
                break;
        }
        this.form.updateValueAndValidity();
    }

    private addVirtualDevice(vdevId: string, serial: string) {
        // if there is only one supported device type, set it from the very beginning
        let autoDevType = this.supportedDevTypes.length === 1 ? this.supportedDevTypes[0] : null;
        // config must be updated in order for mkVirtualDeviceConfig() to work
        this.cfg.devices[vdevId] = {
            type: autoDevType,
            device_serial: serial,
            measures: {}
        };
        const vdevCfg = this.mkVirtualDeviceConfig(vdevId, true);
        this.virtualDevices.push(vdevCfg);
        this.virtualDevices.sort(keySorter('vdevId'));
        this.vDevToExpand = vdevId; // expand panel of newly added device
        // set paginator's page index to show the expanded panel
        this.reloadPages.next(Math.floor(this.virtualDevices.findIndex(v => v.vdevId === vdevId) / this.paginator.pageSize));
        if (autoDevType) {
            this.setDefaultMeasures(vdevCfg, vdevCfg.form.controls['energyProfile'].value, this.getProductionPods(vdevCfg));
        } else {
            this.updateConfig();
            this.updateValidity();
        }
    }

    private renameVirtualDevice(vdevCfg: VirtualDeviceConfig, newId: string, serial: string) {
        const oldDevId = vdevCfg.vdevId;
        vdevCfg.vdevId = newId;
        vdevCfg.form.controls.deviceSerial.setValue(serial);
        this.cfg.devices[newId] = this.cfg.devices[oldDevId];
        this.cfg.devices[newId].device_serial = serial;
        delete this.cfg.devices[oldDevId];
        // rename measures, if somehow "dependent" on device's name
        this.renameVirtualDeviceMeasures(vdevCfg, oldDevId);
        this.virtualDevices.sort(keySorter('vdevId'));
        this.reloadPages.next(0);
        this.updateConfig(); // update once and for all
        // validity does not change, as new name has already been validated
    }

    private deleteVirtualDevice(vdevCfg: VirtualDeviceConfig) {
        vdevCfg.valueSubscription.unsubscribe();
        vdevCfg.statusSubscription.unsubscribe();
        const measures = _.values(vdevCfg.measures);
        const vdevIdx = this.virtualDevices.indexOf(vdevCfg);
        this.virtualDevices.splice(vdevIdx, 1);
        delete this.cfg.devices[vdevCfg.vdevId];
        this.reloadPages.next(0);
        this.updateConfig();
        // validity may change if there are some related measures
        if (measures.length) {
            this.updateValidity();
        }
    }

    private updateSourceConfig(value: { [p: string]: any }) {
        this.cfg.src_cfg.auth_type = value['authType'];
        this.cfg.src_cfg.host = value['host']?.trim();
        this.cfg.src_cfg.port = value['port'];
        switch (this.cfg.src_cfg.auth_type) {
            case AuthType.CREDENTIALS:
                this.cfg.src_cfg.username = value['user'];
                this.cfg.src_cfg.password = this.form.controls.pass?.dirty ? value['pass'] : null;
                break;
            case AuthType.CERTIFICATES:
                break;
        }
    }

    private renameVirtualDeviceMeasures(vdevCfg: VirtualDeviceConfig, oldDevId: string) {
        const nameChanges: { [measId: string]: string } = {}; // old name --> new name map
        const vdev = this.cfg.devices[vdevCfg.vdevId];
        for (let measId in (vdev.measures || {})) {
            const idIdx = measId.indexOf(oldDevId);
            if (idIdx >= 0) {
                nameChanges[measId] = measId.replace(oldDevId, vdevCfg.vdevId);
            }
        }
        for (let measId in nameChanges) {
            const newName = nameChanges[measId];
            vdevCfg.measures[measId].measId = newName;
            vdevCfg.measures[newName] = vdevCfg.measures[measId];
            delete vdevCfg.measures[measId];
            vdev.measures[newName] = vdev.measures[measId];
            delete vdev.measures[measId];
        }
        // NOTE: update is left to the caller
    }

    private mkVirtualDeviceConfig(vdevId: string, newlyCreated: boolean): VirtualDeviceConfig {
        const vdev = this.cfg.devices[vdevId];
        const cfg = {
            vdevId: vdevId,
            newlyCreated
        } as VirtualDeviceConfig;
        cfg.form = this.fb.group({
            devType: new FormControl({
                value: vdev.type,
                disabled: !newlyCreated
            }, [Validators.required]),
            energyProfile: new FormControl(MQTT_DEFAULT_ENERGY_PROFILE),
            productionPlants: new FormControl(1, // ignored for consumers
                [Validators.required, validateInteger, Validators.min(1), this.mkMaxProductionPlantsValidator(cfg)]),
            deviceSerial: new FormControl({
                value: vdev.device_serial,
                disabled: !newlyCreated
            }, [validateNotBlank]),
            phases: new FormControl({value: vdev.phases || Phases.SINGLE, disabled: !newlyCreated}),
            m1K: new FormControl({value: isNonTrivialK(vdev.m1_k) ? vdev.m1_k : 1, disabled: !newlyCreated},
                [Validators.required, validateFloat, Validators.min(1), Validators.max(MQTT_MAX_K)])
        });
        const wrapper = this.getVdevWrapper(vdevId);
        for (let plantIndex = 1; plantIndex <= MAX_PLANTS; ++plantIndex) {
            if (newlyCreated) {
                cfg.form.registerControl(this.mkProductionPlantPodControlName(plantIndex),
                    new FormControl(null,
                        [this.mkProductionPodValidator(cfg.form, plantIndex)]));
            }
            const kValue = !!wrapper && plantIndex <= wrapper.maxProductionPlants ?
                getPlantK(vdev, plantIndex) :
                1;
            cfg.form.registerControl(this.mkProductionPlantKControlName(plantIndex),
                new FormControl({value: isNonTrivialK(kValue) ? kValue : 1, disabled: !newlyCreated},
                    [Validators.required, validateFloat, Validators.min(1), Validators.max(MQTT_MAX_K)]));
        }
        cfg.measures = _.mapValues(vdev.measures || {},
            (meas, measId) => ({
                measId,
                newlyCreated
            }));
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            vdev.type = value['devType'];
            vdev.device_serial = value['deviceSerial']?.trim() || null;
            vdev.phases = value['phases'];
            vdev.m1_k = isNonTrivialK(value['m1K']) ? value['m1K'] : 1;
            const wrapper = this.getVdevWrapper(cfg.vdevId);
            if (wrapper) {
                for (let plantIndex = 1; plantIndex <= wrapper.maxProductionPlants; ++plantIndex) {
                    const k = value[this.mkProductionPlantKControlName(plantIndex)];
                    setPlantK(vdev, plantIndex, isNonTrivialK(k) ? k : 1);
                }
                this.fixDeviceMeter(cfg);
            }
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        if (wrapper) {
            this.fixDeviceMeter(cfg);
        }
        return cfg;
    }

    private mkMaxProductionPlantsValidator(vdevCfg: VirtualDeviceConfig): ValidatorFn {
        return ctrl => {
            const v = ctrl.value;
            const maxProductionPlants = this.getMaxProductionPlants(vdevCfg);
            if (_.isNumber(v) && v > maxProductionPlants) {
                return {'max_prod_pods': true};
            }
            return null;
        };
    }

    private mkProductionPodValidator(vdevForm: FormGroup, plantIndex: number): ValidatorFn {
        return ctrl => {
            if (vdevForm.controls['energyProfile'].invalid || vdevForm.controls['productionPlants'].invalid) {
                return null;
            }
            const profile: MqttEnergyProfile = vdevForm.controls['energyProfile'].value;
            const plants: number = vdevForm.controls['productionPlants'].value;
            if (profile === MqttEnergyProfile.CONSUMER || plantIndex > plants) {
                return null;
            }
            if (!validateProductionPOD(ctrl.value)) {
                return {pod: true};
            }
            // check for production POD uniqueness
            const otherProductionPods: string[] = [];
            for (let plant = 1; plant <= plants; ++plant) {
                if (plant !== plantIndex) {
                    otherProductionPods.push(vdevForm.controls[this.mkProductionPlantPodControlName(plant)].value);
                }
            }
            if (otherProductionPods.find(p => p?.trim() === ctrl.value)) {
                return {unique_pod: true};
            }
            return null;
        };
    }

    private mkPagedVirtualDevices(size: number, page: number): Observable<PagedVirtualDevices> {
        let pagedVdevs = [];
        for (let i = 0; i < this.virtualDevices.length; i += size) {
            pagedVdevs.push(this.virtualDevices.slice(i, i + size));
        }
        return of({
            data: pagedVdevs[page],
            total: this.virtualDevices.length
        } as PagedVirtualDevices);
    }

    private setDefaultMeasures(vdevCfg: VirtualDeviceConfig, energyProfile: MqttEnergyProfile, productionPods: string[]) {
        const vdev = this.cfg.devices[vdevCfg.vdevId];
        // "clear" all measures
        Object.keys(vdev.measures || {}).forEach(mid => {
            delete vdevCfg.measures[mid];
            delete vdev.measures[mid];
        });
        // NOTE: wrapper could be `null` (e.g. if no device type is currently set)
        const wrapper = this.getVdevWrapper(vdevCfg.vdevId);
        if (wrapper) {
            // add default measures from scratch
            wrapper.setDefaultMeasures(energyProfile, productionPods);
            // measures have already been populated: just build the corresponding configs
            for (let measId in vdev.measures) {
                vdevCfg.measures[measId] = {
                    measId,
                    newlyCreated: true
                };
            }
            this.fixDeviceMeter(vdevCfg);
        }
        // final update
        this.updateConfig();
        this.updateValidity();
    }

    private resetProductionPods(vdevCfg: VirtualDeviceConfig) {
        for (let plantIndex = 1; plantIndex <= MAX_PLANTS; ++plantIndex) {
            const podCtrl = vdevCfg.form.controls[this.mkProductionPlantPodControlName(plantIndex)];
            podCtrl.setValue(null, {onlySelf: true, emitEvent: false});
            podCtrl.markAsPristine({onlySelf: true});
        }
        vdevCfg.form.updateValueAndValidity();
    }

    private resetK(vdevCfg: VirtualDeviceConfig) {
        vdevCfg.form.controls['m1K'].setValue(1, {onlySelf: true, emitEvent: false});
        for (let plantIndex = 1; plantIndex <= MAX_PLANTS; ++plantIndex) {
            vdevCfg.form.controls[this.mkProductionPlantKControlName(plantIndex)].setValue(1,
                {onlySelf: true, emitEvent: false});
        }
        vdevCfg.form.updateValueAndValidity();
    }

    private fixDeviceMeter(vdevCfg: VirtualDeviceConfig) {
        const vdev = this.cfg.devices[vdevCfg.vdevId];
        if (!vdev.phases) {
            vdev.phases = Phases.SINGLE;
        }
        let m1K = undefined;
        let m2K = undefined;
        let m22K = undefined;
        let m23K = undefined;
        let m24K = undefined;
        const wrapper = this.getVdevWrapper(vdevCfg.vdevId);
        if (wrapper) {
            const measures = wrapper.getMqttMeasures();
            if (measures && vdev.phases === Phases.THREE) {
                if (!!measures.absorptionMeasures.length || !!measures.injectionMeasures.length) {
                    m1K = isNonTrivialK(vdev.m1_k) ? vdev.m1_k : 1;
                }
                if (wrapper.maxProductionPlants >= 1 && !!measures.productionMeasures[0].length) {
                    const k = getPlantK(vdev, 1);
                    m2K = isNonTrivialK(k) ? k : 1;
                }
                if (wrapper.maxProductionPlants >= 2 && !!measures.productionMeasures[1].length) {
                    const k = getPlantK(vdev, 2);
                    m22K = isNonTrivialK(k) ? k : 1;
                }
                if (wrapper.maxProductionPlants >= 3 && !!measures.productionMeasures[2].length) {
                    const k = getPlantK(vdev, 3);
                    m23K = isNonTrivialK(k) ? k : 1;
                }
                if (wrapper.maxProductionPlants >= 4 && !!measures.productionMeasures[3].length) {
                    const k = getPlantK(vdev, 4);
                    m24K = isNonTrivialK(k) ? k : 1;
                }
            }
        }
        vdev.m1_k = m1K;
        vdev.m2_k = m2K;
        vdev.m2_2_k = m22K;
        vdev.m2_3_k = m23K;
        vdev.m2_4_k = m24K;
    }

    private getProductionPods(vdevCfg: VirtualDeviceConfig): string[] {
        const plants: number = vdevCfg.form.controls['productionPlants'].value || 0;
        const productionPods: string[] = [];
        for (let plantIndex = 1; plantIndex <= plants; ++plantIndex) {
            const podCtrl = vdevCfg.form.controls[this.mkProductionPlantPodControlName(plantIndex)];
            productionPods.push(!podCtrl.invalid ? podCtrl.value : null);
        }
        return productionPods;
    }

    private getVdevWrapper(vdevId: string): MqttDeviceWrapper<any> {
        return wrapMqttDevice(vdevId, this.cfg.devices[vdevId]);
    }

    private loadSupportedDevices() {
        this.apiService.getConnectorSupportedMeters(this.id).subscribe(res => {
            this.supportedDevTypes = res.results.map(meterModel => meterModel2MQTTDevice(meterModel.meter_model))
                .filter(devType => !!devType);
            if (!this.supportedDevTypes.length) {
                // no supported device types: consider all available
                this.supportedDevTypes = MQTT_DEV_TYPES;
            }
        });
    }
}

function getPlantK(vdev: Device, plantIndex: number): number {
    switch (plantIndex) {
        case 1:
            return vdev.m2_k;
        case 2:
            return vdev.m2_2_k;
        case 3:
            return vdev.m2_3_k;
        case 4:
            return vdev.m2_4_k;
        default:
            return 1;
    }
}

function setPlantK(vdev: Device, plantIndex: number, k: number) {
    switch (plantIndex) {
        case 1:
            vdev.m2_k = k;
            break;
        case 2:
            vdev.m2_2_k = k;
            break;
        case 3:
            vdev.m2_3_k = k;
            break;
        case 4:
            vdev.m2_4_k = k;
            break;
    }
}

function isNonTrivialK(k: number): boolean {
    return isDefined(k) && k !== 0 && k !== 1;
}

function mkDeviceId(pod: string, serial: string): string {
    if (!pod?.trim() || !serial?.trim()) {
        return '';
    }
    return `${pod?.trim()}_${serial?.trim()}`;
}

function parseDeviceId(devId: string): PodSerial {
    const underscoreIdx = devId ? devId.indexOf('_') : -1;
    return underscoreIdx > 0 ?
        {pod: devId.substring(0, underscoreIdx), serial: devId.substring(underscoreIdx + 1)} :
        {pod: '', serial: ''};
}
